import ISupplierModel from "@models/ISupplierModel";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

export interface IDailyRoutesFilterLookups {
    routes: (text: string) => Promise<IFilterSelectOption[]>;
    resolveRoute?: (guid: string) => Promise<IFilterSelectOption | undefined>;
    /** Supplier users, for the driver and the created-by filters */
    users: (text: string) => Promise<IFilterSelectOption[]>;
    resolveUser?: (guid: string) => Promise<IFilterSelectOption | undefined>;
    vehicles: (text: string) => Promise<IFilterSelectOption[]>;
    resolveVehicle?: (guid: string) => Promise<IFilterSelectOption | undefined>;
}

export interface IDailyRoutesFilterContext {
    supplierModel: ISupplierModel;
    lookups: IDailyRoutesFilterLookups;
}

/** Baseline Daily Routes filters: the legacy reset clears everything. */
export const DAILY_ROUTES_DEFAULT_FILTERS: FilterValues = {};

/**
 * The query object the Daily Routes FilterBar emits: keys are the `GET /v1/daily_routes` (IGetDailyRoutesDTO)
 * filter fields, which the screen spreads into its REST query next to pagination and sort.
 */
export interface IDailyRoutesFilterQuery {
    name?: unknown;
    routeGuid?: unknown;
    routeDriverUserGuid?: unknown;
    vehicleGuid?: unknown;
    orderName?: unknown;
    routeUserGuid?: unknown;
    startDate?: unknown;
    endDate?: unknown;
}

/**
 * Compile the Daily Routes filter config. Field keys ARE the IGetDailyRoutesDTO field names so presets store
 * what the screen sends. Visibility gates mirror the legacy search (vehicle needs `has_vehicles`).
 */
export function buildDailyRoutesFilterSections(ctx: IDailyRoutesFilterContext): IFilterSection[] {
    const { supplierModel, lookups } = ctx;

    const main: IFilterField[] = [
        { key: "name", label: "name", type: "text", apiKey: "name" },
        {
            key: "routeGuid",
            label: "route",
            type: "typeahead",
            apiKey: "routeGuid",
            lookup: lookups.routes,
            resolveOption: lookups.resolveRoute,
        },
        {
            key: "routeDriverUserGuid",
            label: "driver",
            type: "typeahead",
            apiKey: "routeDriverUserGuid",
            lookup: lookups.users,
            resolveOption: lookups.resolveUser,
        },
    ];

    if (supplierModel.has_vehicles) {
        main.push({
            key: "vehicleGuid",
            label: "vehicle",
            type: "typeahead",
            apiKey: "vehicleGuid",
            lookup: lookups.vehicles,
            resolveOption: lookups.resolveVehicle,
        });
    }

    main.push({ key: "orderName", label: "order", type: "text", apiKey: "orderName" });
    main.push({
        key: "routeUserGuid",
        label: "user",
        type: "typeahead",
        apiKey: "routeUserGuid",
        lookup: lookups.users,
        resolveOption: lookups.resolveUser,
    });

    // The API accepts either bound on its own (>= start, <= end) or both (between).
    const dates: IFilterField[] = [
        { key: "startDate", label: "start_date", type: "date", apiKey: "startDate" },
        { key: "endDate", label: "end_date", type: "date", apiKey: "endDate" },
    ];

    return [
        { id: "main", title: "search", filters: main },
        { id: "dates", title: "date_range", filters: dates },
    ];
}
